/**
 * FormEditor Component
 * Standalone form editing component that loads/edits/saves forms by ID
 * Supports both modal and embedded (via containerSelector) rendering
 * Dynamically hides/shows UI elements based on renderOptions
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { FormBuilderCanvas } from '@atlas-forms/designer-components-react';
import { AtlasFormsClient } from '@atlas-forms/client-js';
import type { FormSchema, RenderOptions, RenderOptionsResolved } from '@atlas-forms/types-js';
import { resolveRenderOptions } from '@atlas-forms/types-js';
import type { FormEditorProps } from '../types';
import './FormEditor.css';

/**
 * Merge legacy individual props with new renderOptions
 * renderOptions takes precedence if both are provided
 * Used for backward compatibility with old FormEditor props
 */
function mergeRenderOptions(
  providedOptions?: RenderOptions,
  legacyProps?: {
    renderMode?: 'modal' | 'inline';
    autoSave?: boolean;
    autoSaveInterval?: number;
  },
): RenderOptionsResolved {
  // Start with provided renderOptions
  let merged: Partial<RenderOptions> = { ...providedOptions };

  // If no containerSelector provided, check legacy renderMode
  if (!merged.containerSelector && legacyProps?.renderMode === 'inline') {
    // Don't set containerSelector — inline mode is handled separately
    merged.containerSelector = undefined;
  }

  // Resolve with defaults
  return resolveRenderOptions(merged);
}

/**
 * Safely resolve target element for embedded rendering
 * Returns null and logs warning if selector invalid or element not found
 * Falls back to modal rendering when null
 */
function resolveTargetElement(selector?: string): HTMLElement | null {
  if (!selector) return null;

  try {
    const element = document.querySelector(selector);
    if (!element) {
      console.warn(`[FormEditor] containerSelector not found: "${selector}". Falling back to modal.`);
      return null;
    }
    return element as HTMLElement;
  } catch (err) {
    console.error(`[FormEditor] Invalid containerSelector: "${selector}". Falling back to modal.`, err);
    return null;
  }
}

export const FormEditor: React.FC<FormEditorProps> = ({
  formId,
  renderOptions: providedRenderOptions,
  renderMode = 'modal',
  onSave,
  onClose,
  onError,
  modalTitle,
  showLoading = true,
  className = '',
  autoSave = false,
  autoSaveInterval = 30000,
}) => {
  // Merge legacy props with new renderOptions
  const renderOptions = useMemo(
    () =>
      mergeRenderOptions(providedRenderOptions, {
        renderMode,
        autoSave,
        autoSaveInterval,
      }),
    [providedRenderOptions, renderMode, autoSave, autoSaveInterval],
  );

  const [formSchema, setFormSchema] = useState<FormSchema | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Determine rendering strategy (embedded vs modal)
  const targetElement = useMemo(
    () => resolveTargetElement(renderOptions.containerSelector),
    [renderOptions.containerSelector],
  );
  const isEmbedded = !!targetElement;

  // Load form schema on mount or when formId changes
  useEffect(() => {
    const loadForm = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const client = AtlasFormsClient.getInstance();
        const schema = await client.loadFormDefinition(formId);
        setFormSchema(schema);
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to load form';
        setError(errorMsg);
        onError?.(err instanceof Error ? err : new Error(errorMsg));
      } finally {
        setIsLoading(false);
      }
    };

    loadForm();
  }, [formId, onError]);

  // Handle schema changes
  const handleSchemaChange = useCallback(
    (newSchema: FormSchema) => {
      setFormSchema(newSchema);

      // Auto-save if enabled
      if (autoSave) {
        if (autoSaveTimerRef.current) {
          clearTimeout(autoSaveTimerRef.current);
        }

        autoSaveTimerRef.current = setTimeout(() => {
          handleSave(newSchema);
        }, autoSaveInterval);
      }
    },
    [autoSave, autoSaveInterval],
  );

  // Handle save
  const handleSave = useCallback(
    async (schema?: FormSchema) => {
      const schemaToSave = schema || formSchema;
      if (!schemaToSave) return;

      try {
        setIsSaving(true);
        setError(null);

        if (onSave) {
          // Use custom save handler if provided
          await onSave(schemaToSave);
        }
        // Note: Actual API save is handled by FormBuilderCanvas or custom onSave handler
      } catch (err) {
        const errorMsg = err instanceof Error ? err.message : 'Failed to save form';
        setError(errorMsg);
        onError?.(err instanceof Error ? err : new Error(errorMsg));
      } finally {
        setIsSaving(false);
      }
    },
    [formSchema, onSave, onError],
  );

  // Handle cancel
  const handleCancel = useCallback(() => {
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }
    onClose?.();
  }, [onClose]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  // Render FormBuilderCanvas with visibility props from renderOptions
  const editorContent = (
    <>
      {error && <div className="fe-error-message">{error}</div>}

      {isLoading && showLoading ? (
        <div className="fe-loading">
          <div className="fe-spinner"></div>
          <p>Loading form...</p>
        </div>
      ) : formSchema ? (
        <FormBuilderCanvas
          initialSchema={formSchema}
          onSchemaChange={handleSchemaChange}
          onSave={handleSave}
          hideDesignerTab={renderOptions.hideDesignerTab}
          hideJsonTab={renderOptions.hideJsonTab}
          hideSettingsTab={renderOptions.hideSettingsTab}
          hideNavigationTabs={renderOptions.hideNavigationTabs}
          hidePropertiesPanel={renderOptions.hidePropertiesPanel}
          hideFieldToolbox={renderOptions.hideFieldToolbox}
          hideSaveButton={renderOptions.hideSaveButton}
          defaultTab={renderOptions.defaultTab}
        />
      ) : (
        <div className="fe-error-message">Failed to load form</div>
      )}
    </>
  );

  // Embedded rendering mode (render into target element via Portal)
  if (isEmbedded && targetElement) {
    return createPortal(
      <div className={`fe-embedded-editor ${className}`}>{editorContent}</div>,
      targetElement,
    );
  }

  // Modal rendering mode (default when no containerSelector or element not found)
  return (
    <div className="fe-modal-overlay">
      <div className={`fe-modal ${className}`}>
        <div className="fe-modal-header">
          <h2 className="fe-modal-title">
            {modalTitle || `Edit Form: ${formSchema?.metadata?.title || `Form #${formId}` || 'Loading...'}`}
          </h2>
          <button
            className="fe-modal-close"
            onClick={handleCancel}
            aria-label="Close"
            disabled={isSaving}
          >
            ×
          </button>
        </div>

        <div className="fe-modal-body">{editorContent}</div>

        <div className="fe-modal-footer">
          <button
            className="fe-btn fe-btn--secondary"
            onClick={handleCancel}
            disabled={isSaving}
          >
            Cancel
          </button>
          <button
            className="fe-btn fe-btn--primary"
            onClick={() => handleSave()}
            disabled={isSaving || isLoading || !formSchema}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

FormEditor.displayName = 'FormEditor';
